import { Skeleton, useToast } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import _ from 'lodash';
import React from 'react';
import { useHistory } from 'react-router-dom';

import { finleyAssert, date as commonDate } from '@finley-technologies/common';

import { CreditFacilityGQLType } from 'types/dashboard-core-gqlTypes.generated';

import { formatPortfolioOverviewFacilityRow } from './PortfoliioOverviewFacilitiesTableUtils';
import { PortfolioOverviewFacilitiesTable } from './PortfolioOverviewFacilitiesTable';
import { useGetLatestCovenantCalculationsAsCapitalProviderQuery } from 'packages/borrowing-base/requests/financialCovenants.gql.generated';
import {
  errorToastConfig,
  errorToastId,
  getDefaultDashboardDisplayStartDate,
} from 'packages/common-frontend/constants';
import { useGetCurrentCreditAgreementFullArtifactsForOrganizationQuery } from 'packages/creditAgreements/requests/requests.gql.generated';
import { parseCreditAgreementFullArtifact } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { useGetApprovedFundingRequestsMetadataQuery } from 'packages/funding-requests/requests/requests.gql.generated';
import { useGetLatestLedgersForCreditFacilitiesQuery } from 'packages/statements/requests/ledger.gql.generated';

export const PortfolioOverviewFacilitiesContainer: React.FC<{
  organizationId: string;
  creditFacilities: Pick<CreditFacilityGQLType, 'id' | 'slug'>[];
}> = withLDConsumer()(({ flags, organizationId, creditFacilities }) => {
  const toast = useToast();
  const history = useHistory();

  const creditFacilityIds = _.map(creditFacilities, ({ id }) => id);
  const startDate = getDefaultDashboardDisplayStartDate();

  const {
    loading: creditAgreementArtifactsLoading,
    data: creditAgreementArtifactsData,
  } = useGetCurrentCreditAgreementFullArtifactsForOrganizationQuery({
    variables: {
      organizationId,
      creditFacilitySlugsToHide: flags?.creditFacilitySlugsToHide ?? [],
    },
    onError: () => {
      if (!toast.isActive(errorToastId)) {
        errorToastConfig.title = `Error loading current credit agreements for organization.`;
        toast(errorToastConfig);
      }
    },
    fetchPolicy: 'network-only',
  });

  const {
    loading: approvedFundingRequestsLoading,
    data: approvedFundingRequestsData,
  } = useGetApprovedFundingRequestsMetadataQuery({
    variables: {
      creditFacilityIds,
      startDate: commonDate.fullDateStrToEpoch(startDate),
    },
  });

  const {
    loading: ledgerLoading,
    data: ledgerData,
  } = useGetLatestLedgersForCreditFacilitiesQuery({
    variables: {
      creditFacilityIds,
      countersignerOrganizationIds: [organizationId],
      ledgerAccountGroup: 'expensesFees',
    },
  });

  const {
    loading: covenantsLoading,
    data: covenantsData,
  } = useGetLatestCovenantCalculationsAsCapitalProviderQuery({
    variables: { creditFacilityIds },
  });

  if (
    creditAgreementArtifactsLoading ||
    !creditAgreementArtifactsData ||
    approvedFundingRequestsLoading ||
    !approvedFundingRequestsData ||
    ledgerLoading ||
    !ledgerData ||
    covenantsLoading ||
    !covenantsData
  ) {
    return <Skeleton height="600px" />;
  }

  const {
    organization: { currentCreditAgreementArtifacts },
  } = creditAgreementArtifactsData;

  let parsedCreditAgreementFullArtifacts = null;

  try {
    parsedCreditAgreementFullArtifacts = _.map(
      currentCreditAgreementArtifacts,
      (creditAgreementFullArtifact) => {
        const { creditAgreement, id } = creditAgreementFullArtifact;
        const parsedCreditAgreement = parseCreditAgreementFullArtifact(
          creditAgreement,
        );
        finleyAssert(
          parsedCreditAgreement,
          `Expect creditAgreementFullArtifact to exist for creditAgreementId ${id}`,
        );
        return parsedCreditAgreement;
      },
    );

    const rows = formatPortfolioOverviewFacilityRow({
      creditAgreements: parsedCreditAgreementFullArtifacts,
      fundingRequests: approvedFundingRequestsData.fundingRequests.nodes,
      ledgerBalances: ledgerData.latestLedgersForCreditFacilities,
      covenantCalculations:
        covenantsData.latestCovenantCalculationsAsCapitalProvider,
      creditFacilities,
      organizationId,
    });

    return (
      <PortfolioOverviewFacilitiesTable portfolioOverviewFacilityRows={rows} />
    );
  } catch (err) {
    Sentry.captureException(err);
    const errorToastTitle = `Error parsing credit agreement artifacts for organization ${organizationId}`;
    toast({
      ...errorToastConfig,
      title: errorToastTitle,
      id: 'errorParsingCreditAgreement',
    });
    history.push('/');
  }

  throw new Error('unreachable code here');
});
